import { ApiService } from "./api.service";
import type { ExportStatus } from "@/components/data-table/use-csv-export";
import type {
  TSkill,
  TSkillBulkImportResult,
  TSkillBulkImportRow,
  TUserSkillAssignment,
  TWorkspaceSkillsListQuery,
  TWorkspaceSkillsPage,
} from "@/types/skill";

function buildListQuery(params: TWorkspaceSkillsListQuery): string {
  const search = new URLSearchParams();
  if (params.archived !== undefined) search.set("archived", params.archived ? "true" : "false");
  if (params.q) search.set("q", params.q);
  if (params.user_ids && params.user_ids.length > 0) {
    search.set("user_ids", params.user_ids.join(","));
  }
  if (params.cursor) search.set("cursor", params.cursor);
  if (params.per_page != null) search.set("per_page", String(params.per_page));
  if (params.order_by) search.set("order_by", params.order_by);
  const str = search.toString();
  return str ? `?${str}` : "";
}

const FULL_LIST_PER_PAGE = 200;

export class WorkspaceSkillService extends ApiService {
  /** Single-page fetch — returns the paginated envelope. The management list
   *  uses this directly; pickers that need every skill should use `fetchAll`. */
  async list(slug: string, params: TWorkspaceSkillsListQuery = {}): Promise<TWorkspaceSkillsPage> {
    return this.get(`/api/workspaces/${slug}/skills/${buildListQuery(params)}`);
  }

  /**
   * Export the skills list as CSV, scoped to the same filters/sort as the list
   * (`params`) and, when `body.ids` is set, restricted to those rows. Returns
   * the raw Response (200 stream / 202 async token).
   */
  async exportSkills(
    slug: string,
    body: { ids?: string[]; columns?: string[] },
    params: TWorkspaceSkillsListQuery = {},
  ): Promise<Response> {
    return this.postRaw(`/api/workspaces/${slug}/export-skills/${buildListQuery(params)}`, body);
  }

  async getExportStatus(slug: string, token: string): Promise<ExportStatus> {
    return this.get(`/api/workspaces/${slug}/export-status/${token}/`);
  }

  /** Walks every page and flattens into a plain `TSkill[]`. Used by callers
   *  that legitimately need the full live bank (intake palette, profile
   *  skills picker, team sidebar, availability page) — these never paginate. */
  async fetchAll(slug: string, params: TWorkspaceSkillsListQuery = {}): Promise<TSkill[]> {
    const all: TSkill[] = [];
    let cursor: string | undefined;
    while (true) {
      const page: TWorkspaceSkillsPage = await this.list(slug, {
        ...params,
        per_page: params.per_page ?? FULL_LIST_PER_PAGE,
        cursor,
      });
      all.push(...page.results);
      if (!page.next_page_results || !page.next_cursor) break;
      cursor = page.next_cursor;
    }
    return all;
  }

  async create(slug: string, data: { name: string }): Promise<TSkill> {
    return this.post(`/api/workspaces/${slug}/skills/`, data);
  }

  async update(
    slug: string,
    skillId: string,
    data: Partial<Pick<TSkill, "name" | "sort_order">>,
  ): Promise<TSkill> {
    return this.patch(`/api/workspaces/${slug}/skills/${skillId}/`, data);
  }

  /** Bulk-create (or reuse) skills from parsed CSV rows, optionally assigning
   *  users by email. Returns the per-row report (created/reused/error + skipped
   *  assignees) so the importer can show partial success. */
  async bulkImport(slug: string, rows: TSkillBulkImportRow[]): Promise<TSkillBulkImportResult> {
    return this.post(`/api/workspaces/${slug}/skills/bulk-import/`, { rows });
  }

  async remove(slug: string, skillId: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/skills/${skillId}/`);
  }

  async archive(slug: string, skillId: string): Promise<TSkill> {
    return this.post(`/api/workspaces/${slug}/skills/${skillId}/archive/`, {});
  }

  async unarchive(slug: string, skillId: string): Promise<TSkill> {
    return this.post(`/api/workspaces/${slug}/skills/${skillId}/unarchive/`, {});
  }

  async listSkillUsers(slug: string, skillId: string): Promise<TUserSkillAssignment[]> {
    // Server-paginated (issue #1254); walk the pages so the skill picker keeps
    // the full set of assigned users it renders locally. UI is unchanged.
    return this.getAllPages<TUserSkillAssignment>(({ cursor, perPage }) => {
      const qs = new URLSearchParams({ per_page: String(perPage) });
      if (cursor) qs.set("cursor", cursor);
      return `/api/workspaces/${slug}/skills/${skillId}/users/?${qs}`;
    });
  }

  async setSkillUsers(
    slug: string,
    skillId: string,
    userIds: string[],
  ): Promise<TUserSkillAssignment[]> {
    return this.put(`/api/workspaces/${slug}/skills/${skillId}/users/`, {
      user_ids: userIds,
    });
  }

  async listMySkills(slug: string): Promise<TUserSkillAssignment[]> {
    return this.get(`/api/workspaces/${slug}/members/me/skills/`);
  }

  async setMySkills(slug: string, skillIds: string[]): Promise<TUserSkillAssignment[]> {
    return this.post(`/api/workspaces/${slug}/members/me/skills/`, { skill_ids: skillIds });
  }

  async listMemberSkills(slug: string, memberId: string): Promise<TUserSkillAssignment[]> {
    return this.get(`/api/workspaces/${slug}/members/${memberId}/skills/`);
  }

  async setMemberSkills(
    slug: string,
    memberId: string,
    skillIds: string[],
  ): Promise<TUserSkillAssignment[]> {
    return this.put(`/api/workspaces/${slug}/members/${memberId}/skills/`, {
      skill_ids: skillIds,
    });
  }

  // Qualifications on a work item. A *project* work item must use the
  // project-scoped endpoint (the workspace-scoped one rejects project items so
  // project RBAC stays authoritative); a *project-less* one uses the
  // workspace-scoped path. Mirrors workItemService.subWorkItemsPath — without
  // the branch, a project-less event (project_id null → "") builds
  // `…/projects//issues/…` and 404s, since `<uuid:project_id>` cannot match an
  // empty segment, which is how a qualification silently never saved (NG-1883).
  private issueSkillsPath(
    slug: string,
    projectId: string | null | undefined,
    issueId: string,
  ): string {
    return projectId
      ? `/api/workspaces/${slug}/projects/${projectId}/issues/${issueId}/skills/`
      : `/api/workspaces/${slug}/work-items/${issueId}/skills/`;
  }

  async addIssueSkills(
    slug: string,
    projectId: string | null | undefined,
    issueId: string,
    skillIds: string[],
  ): Promise<void> {
    return this.post(this.issueSkillsPath(slug, projectId, issueId), {
      skill_ids: skillIds,
    });
  }

  async removeIssueSkill(
    slug: string,
    projectId: string | null | undefined,
    issueId: string,
    skillId: string,
  ): Promise<void> {
    return this.delete(`${this.issueSkillsPath(slug, projectId, issueId)}${skillId}/`);
  }
}
